/**
 * 2026-04-25 Phase 32-C.7 — 망치 타격 연출.
 *
 * <p>유저가 "첫 타격" 또는 "한 번 더" 를 누르는 순간의 긴장감을 담당.
 *
 * <p>3 단계:
 * <ol>
 *   <li><b>windup</b> (600ms): 모루 주변 빛, 떨리는 "…", 정지된 듯한 공기. 심장박동 고속.</li>
 *   <li><b>impact</b> (140ms): 화면 flash → 모달 강한 shake → 낮은 "쿵" 사운드.</li>
 *   <li><b>reveal</b> (420ms): 시퀀스에 새 글자(S 금빛 / F 붉은 타격) 가 낙인찍히며 드러남.</li>
 * </ol>
 *
 * <p>API 호출은 이 연출과 병렬로 진행되며, 실제 결과는 reveal 시점에 부모 컴포넌트가 넘겨준다.
 * 네트워크가 느리면 windup 이 늘어나고, 빠르면 최소 windup 을 보장한다.
 */
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { tokens } from "@/art/tokens";

export type AnvilStrikePhase = "windup" | "impact" | "reveal";

interface Props {
    /** 이 스트라이크의 시퀀스 문자 — null 이면 아직 API 대기 중. */
    stepSuccess: boolean | null;
    /** 이번 스트라이크가 몇 번째인지 (1~4). sequence 의 몇 번째 슬롯에 꽂히는지 결정. */
    stepIndex: number;
    /** 연출 끝나면 부모에 알림. */
    onComplete: () => void;
}

const WINDUP_MIN_MS = 620;
const IMPACT_MS = 140;
const REVEAL_MS = 420;

export function AnvilStrike({ stepSuccess, stepIndex, onComplete }: Props) {
    const [phase, setPhase] = useState<AnvilStrikePhase>("windup");
    const startedAt = useRef(performance.now());

    // 결과가 도착했고 windup 최소 시간이 지났으면 impact 로 전이
    useEffect(() => {
        if (phase !== "windup") return;
        if (stepSuccess === null) return;  // API 아직
        const elapsed = performance.now() - startedAt.current;
        const wait = Math.max(0, WINDUP_MIN_MS - elapsed);
        const id = setTimeout(() => setPhase("impact"), wait);
        return () => clearTimeout(id);
    }, [phase, stepSuccess]);

    // impact → reveal → complete
    useEffect(() => {
        if (phase === "impact") {
            playThud(stepSuccess ?? false);
            const id = setTimeout(() => setPhase("reveal"), IMPACT_MS);
            return () => clearTimeout(id);
        }
        if (phase === "reveal") {
            const id = setTimeout(() => onComplete(), REVEAL_MS);
            return () => clearTimeout(id);
        }
    }, [phase, stepSuccess, onComplete]);

    const isSuccess = stepSuccess === true;
    const accent = isSuccess ? tokens.color.brass : tokens.color.burgundy;

    return (
        <div style={overlayStyle} aria-hidden>
            {/* WINDUP: 어두운 비네팅 + 모루 후광 + "…" */}
            <AnimatePresence>
                {phase === "windup" && (
                    <motion.div
                        key="vignette"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        style={vignetteStyle}
                    />
                )}
                {phase === "windup" && (
                    <motion.div
                        key="halo"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{
                            opacity: [0.3, 0.7, 0.4, 0.85],
                            scale: [0.9, 1.02, 0.96, 1.06],
                        }}
                        transition={{ duration: WINDUP_MIN_MS / 1000, ease: "easeInOut" }}
                        style={{
                            position: "absolute",
                            width: 320,
                            height: 320,
                            borderRadius: "50%",
                            border: `1px solid ${tokens.color.brass}66`,
                            boxShadow: `0 0 80px ${tokens.color.brass}55, inset 0 0 40px ${tokens.color.brass}33`,
                        }}
                    />
                )}
                {phase === "windup" && (
                    <motion.div
                        key="dots"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: [0, 0.9, 0.7, 1], y: 0 }}
                        transition={{ duration: WINDUP_MIN_MS / 1000 }}
                        style={windupTextStyle}
                    >
                        <motion.span
                            animate={{ opacity: [0.4, 1, 0.4] }}
                            transition={{ duration: 0.6, repeat: Infinity }}
                        >
                            망치가 숨을 멈춘다…
                        </motion.span>
                    </motion.div>
                )}

                {/* IMPACT: 화면 flash (성공=금빛, 실패=붉은빛) */}
                {phase === "impact" && (
                    <motion.div
                        key="flash"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 0.8, 0] }}
                        transition={{ duration: IMPACT_MS / 1000, times: [0, 0.25, 0.5, 1] }}
                        style={{
                            position: "absolute",
                            inset: "-50%",
                            background: accent,
                            width: "200%",
                            height: "200%",
                        }}
                    />
                )}

                {/* REVEAL: 큰 글자가 슬롯에 꽂히듯 */}
                {phase === "reveal" && (
                    <motion.div
                        key="reveal-letter"
                        initial={{ opacity: 0, scale: 2.2, y: -10 }}
                        animate={{ opacity: [0, 1, 1, 0], scale: [2.2, 1.1, 1.0, 0.92], y: [-10, 0, 2, 8] }}
                        transition={{ duration: REVEAL_MS / 1000, times: [0, 0.2, 0.75, 1] }}
                        style={{
                            position: "absolute",
                            fontSize: 140,
                            fontWeight: 900,
                            color: accent,
                            textShadow: `0 0 40px ${accent}, 0 0 80px ${accent}99`,
                            fontFamily: "serif",
                            letterSpacing: 4,
                        }}
                    >
                        {isSuccess ? "✦" : "✕"}
                    </motion.div>
                )}
                {phase === "reveal" && (
                    <motion.div
                        key="reveal-label"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: [0, 0.95, 0.8, 0], y: [10, 0, -2, -8] }}
                        transition={{ duration: REVEAL_MS / 1000, delay: 0.08 }}
                        style={{
                            position: "absolute",
                            top: "62%",
                            fontSize: 22,
                            color: accent,
                            letterSpacing: 8,
                            textShadow: `0 0 16px ${accent}`,
                            fontFamily: "serif",
                        }}
                    >
                        {isSuccess ? "성공" : "실패"} · 제 {stepIndex} 타
                    </motion.div>
                )}
            </AnimatePresence>

            {/* IMPACT 순간 큰 원형 충격파 */}
            {phase === "impact" && (
                <motion.div
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ scale: 4, opacity: 0 }}
                    transition={{ duration: IMPACT_MS / 1000, ease: "easeOut" }}
                    style={{
                        position: "absolute",
                        width: 180,
                        height: 180,
                        borderRadius: "50%",
                        border: `4px solid ${accent}`,
                        boxShadow: `0 0 60px ${accent}`,
                    }}
                />
            )}
        </div>
    );
}

// ──── 사운드 ────
// Web Audio 로 낮은 "쿵" — 성공/실패 에 따라 피치 다르게.
function playThud(success: boolean) {
    try {
        const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(success ? 90 : 60, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(success ? 45 : 30, ctx.currentTime + 0.22);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.7, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.35);
        // 성공이면 위에 얹을 높은 톤 하나 더 (brass 느낌)
        if (success) {
            const osc2 = ctx.createOscillator();
            const g2 = ctx.createGain();
            osc2.type = "triangle";
            osc2.frequency.setValueAtTime(520, ctx.currentTime + 0.01);
            osc2.frequency.exponentialRampToValueAtTime(280, ctx.currentTime + 0.28);
            g2.gain.setValueAtTime(0.0001, ctx.currentTime + 0.01);
            g2.gain.exponentialRampToValueAtTime(0.28, ctx.currentTime + 0.03);
            g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
            osc2.connect(g2).connect(ctx.destination);
            osc2.start(ctx.currentTime + 0.01);
            osc2.stop(ctx.currentTime + 0.35);
        }
    } catch {
        // Audio 차단 환경 — 조용히 무시
    }
}

// ──── 스타일 ────
const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1500,
    pointerEvents: "none",
    overflow: "hidden",
};

const vignetteStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    background: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.82) 100%)",
};

const windupTextStyle: React.CSSProperties = {
    position: "absolute",
    top: "66%",
    fontSize: 17,
    color: tokens.color.parchment,
    letterSpacing: 4,
    fontFamily: "serif",
    textShadow: "0 2px 12px rgba(14,12,24,0.95)",
};
